export const STANDARD_CATEGORY_IDS = [
  "produce",
  "bakery",
  "dairy",
  "meat",
  "seafood",
  "pantry",
  "sweets",
  "beverages",
  "household",
  "personal",
  "frozen",
  "diy",
  "international",
  "other",
] as const;

export type StandardCategoryId = (typeof STANDARD_CATEGORY_IDS)[number];

const legacyPrefix = "grocery.category.";
const standardWebKeys = new Set<string>(
  STANDARD_CATEGORY_IDS.map((id) => `${legacyPrefix}${id}`),
);

const legacyAliases: Record<string, string> = {
  "grocery.category.snacks": "grocery.category.sweets",
  "grocery_list.category.snacks": "grocery.category.sweets",
  "grocery.category.condiments": "grocery.category.pantry",
  "grocery_list.category.condiments": "grocery.category.pantry",
  "grocery.category.spices": "grocery.category.pantry",
  "grocery_list.category.spices": "grocery.category.pantry",
  "grocery.category.personal_care": "grocery.category.personal",
  "grocery_list.category.personal_care": "grocery.category.personal",
};

export const DEFAULT_CATEGORIES: string[] = STANDARD_CATEGORY_IDS.map(
  (id) => `${legacyPrefix}${id}`,
);

export const FALLBACK_CATEGORY = "grocery.category.other";

export const normalizeStandardCategoryKey = (
  category: string | null | undefined,
): string | null => {
  if (!category) return null;

  if (standardWebKeys.has(category)) {
    return category;
  }

  const alias = legacyAliases[category];
  if (alias) {
    return alias;
  }

  if (category.startsWith("grocery_list.category.")) {
    const legacyKey = category.replace("grocery_list.category.", legacyPrefix);
    return standardWebKeys.has(legacyKey) ? legacyKey : null;
  }

  return null;
};

export const getStandardCategoryId = (
  category: string | null | undefined,
): StandardCategoryId | null => {
  const normalized = normalizeStandardCategoryKey(category);
  if (!normalized) return null;

  const id = normalized.replace(legacyPrefix, "") as StandardCategoryId;
  return STANDARD_CATEGORY_IDS.includes(id) ? id : null;
};

export const getStandardCategoryTranslationKey = (
  category: string | null | undefined,
): string | null => {
  const id = getStandardCategoryId(category);
  return id ? `sharedGroceryList.category_${id}` : null;
};
